import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Save, FileDown, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SignatureCanvas } from "@/components/SignatureCanvas";
import { exportWarningToPDF } from "@/lib/contractPdfExport";
import { saveDocumentToEmployeeFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { PdfPreviewDialog } from "./PdfPreviewDialog";

interface Employee {
  id: string;
  name: string;
  position: string;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
  department: string | null;
}

interface CompanySettings {
  name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
}

export const WarningForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  
  const [formData, setFormData] = useState({
    selectedEmployeeId: "",
    personName: "",
    personAddress: "",
    position: "",
    department: "",
    documentDate: new Date().toISOString().split("T")[0],
    warningDate: new Date().toISOString().split("T")[0],
    warningNumber: 1,
    warningReason: "",
    warningDetails: "",
    warningConsequences: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, companyRes] = await Promise.all([
        supabase.from("employees").select("id, name, position, address_street, address_zip, address_city, department").is("archived_at", null),
        supabase.from("company_settings").select("name, street, house_number, postal_code, city").single()
      ]);

      if (employeesRes.data) setEmployees(employeesRes.data);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = async (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (!employee) return;

    // Count existing warnings for this employee
    const { count } = await supabase
      .from("contract_templates")
      .select("*", { count: "exact", head: true })
      .eq("employee_id", employeeId)
      .eq("template_type", "warning");

    const warningNumber = (count || 0) + 1;

    setFormData(prev => ({
      ...prev,
      selectedEmployeeId: employeeId,
      personName: employee.name,
      personAddress: `${employee.address_street || ""}, ${employee.address_zip || ""} ${employee.address_city || ""}`,
      position: employee.position,
      department: employee.department || "",
      warningNumber,
      warningConsequences: getDefaultConsequences(warningNumber),
    }));
  };

  const getDefaultConsequences = (warningNumber: number) => {
    switch (warningNumber) {
      case 1:
        return "Bei Wiederholung des Fehlverhaltens behalten wir uns weitere arbeitsrechtliche Schritte, einschließlich einer weiteren Abmahnung oder der Kündigung des Arbeitsverhältnisses, vor.";
      case 2:
        return "Dies ist bereits die zweite Abmahnung. Bei einem erneuten Verstoß werden wir das Arbeitsverhältnis fristlos oder fristgemäß kündigen.";
      default:
        return "Nach mehrfacher Abmahnung werden wir bei einem weiteren Verstoß das Arbeitsverhältnis unverzüglich kündigen.";
    }
  };

  const handleSave = async (signatureUrl?: string) => {
    if (!user) return;
    if (!formData.selectedEmployeeId || !formData.warningReason || !formData.warningDetails) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("contract_templates").insert({
        template_type: "warning",
        employee_id: formData.selectedEmployeeId,
        person_name: formData.personName,
        person_address_street: formData.personAddress,
        position: formData.position,
        department: formData.department,
        warning_date: formData.warningDate,
        warning_number: formData.warningNumber,
        warning_reason: formData.warningReason,
        warning_details: formData.warningDetails,
        warning_consequences: formData.warningConsequences,
        signature_url: signatureUrl,
        signed_at: signatureUrl ? new Date().toISOString() : null,
        signed_by: signatureUrl ? formData.selectedEmployeeId : null,
        status: signatureUrl ? "signed" : "draft",
        created_by: user.id,
      });

      if (error) throw error;

      // Create + archive PDF to employee documents
      try {
        const pdfDoc = await exportWarningToPDF(
          {
            personName: formData.personName,
            personAddress: formData.personAddress,
            position: formData.position,
            department: formData.department,
            documentDate: formData.documentDate,
            warningDate: formData.warningDate,
            warningNumber: formData.warningNumber,
            warningReason: formData.warningReason,
            warningDetails: formData.warningDetails,
            warningConsequences: formData.warningConsequences,
            companyName: companySettings?.name || "",
            companyAddress: companySettings
              ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}`
              : "",
          },
          false
        );

        const category = getDocumentCategory("warning");
        const typeName = getDocumentTypeName("warning");
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} Nr. ${formData.warningNumber} - ${formData.warningReason}`,
          category,
          pdfDoc,
          user.id
        );

        if (result.success) {
          toast.success("Abmahnung gespeichert und in Dokumente abgelegt");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
          toast.error("Abmahnung gespeichert, PDF-Ablage fehlgeschlagen");
        }
      } catch (e) {
        console.error("Error archiving PDF:", e);
        toast.error("Abmahnung gespeichert, PDF-Ablage fehlgeschlagen");
      }

      setShowSignature(false);
      resetForm();
    } catch (error) {
      console.error("Error saving warning:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const buildPdfPayload = () => ({
    personName: formData.personName,
    personAddress: formData.personAddress,
    position: formData.position,
    department: formData.department,
    documentDate: formData.documentDate,
    warningDate: formData.warningDate,
    warningNumber: formData.warningNumber,
    warningReason: formData.warningReason,
    warningDetails: formData.warningDetails,
    warningConsequences: formData.warningConsequences,
    companyName: companySettings?.name || "",
    companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
  });

  const handlePreview = async () => {
    if (!formData.personName || !formData.warningReason) {
      toast.error("Bitte füllen Sie mindestens Mitarbeiter und Abmahnungsgrund aus");
      return;
    }
    try {
      const pdfDoc = await exportWarningToPDF(buildPdfPayload(), false);
      setPreviewDoc(pdfDoc);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Error generating preview:", e);
      toast.error("Fehler beim Erstellen der Vorschau");
    }
  };

  const handleExportPDF = async () => {
    if (!formData.selectedEmployeeId || !formData.warningReason || !formData.warningDetails) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    try {
      // Pass saveToFile: false to get the PDF document without auto-downloading
      const pdfDoc = await exportWarningToPDF({
        personName: formData.personName,
        personAddress: formData.personAddress,
        position: formData.position,
        department: formData.department,
        documentDate: formData.documentDate,
        warningDate: formData.warningDate,
        warningNumber: formData.warningNumber,
        warningReason: formData.warningReason,
        warningDetails: formData.warningDetails,
        warningConsequences: formData.warningConsequences,
        companyName: companySettings?.name || "",
        companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
      }, false);

      // Save to employee documents
      if (user) {
        const category = getDocumentCategory('warning');
        const typeName = getDocumentTypeName('warning');
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} Nr. ${formData.warningNumber} - ${formData.warningReason}`,
          category,
          pdfDoc,
          user.id
        );
        if (result.success) {
          toast.success("PDF wurde erstellt und in den Mitarbeiter-Dokumenten gespeichert");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
        }
      }

      // Always download the PDF for the user
      pdfDoc.save(`Abmahnung_${formData.personName.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      selectedEmployeeId: "",
      personName: "",
      personAddress: "",
      position: "",
      department: "",
      documentDate: new Date().toISOString().split("T")[0],
      warningDate: new Date().toISOString().split("T")[0],
      warningNumber: 1,
      warningReason: "",
      warningDetails: "",
      warningConsequences: "",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          Abmahnung erstellen
        </CardTitle>
        <CardDescription>
          Erstellen Sie eine rechtlich konforme Abmahnung
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Employee Selection */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Mitarbeiter auswählen</h4>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Mitarbeiter *</Label>
              <Select value={formData.selectedEmployeeId} onValueChange={handleEmployeeSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dokumentendatum</Label>
              <Input 
                type="date"
                value={formData.documentDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, documentDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Datum der Abmahnung *</Label>
              <Input 
                type="date"
                value={formData.warningDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, warningDate: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Name</Label>
              <Input value={formData.personName} readOnly className="bg-muted" />
            </div>
            <div>
              <Label>Position</Label>
              <Input value={formData.position} readOnly className="bg-muted" />
            </div>
            <div>
              <Label>Abmahnung Nr.</Label>
              <Input 
                type="number"
                value={formData.warningNumber} 
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  warningNumber: parseInt(e.target.value) || 1,
                  warningConsequences: getDefaultConsequences(parseInt(e.target.value) || 1)
                }))}
              />
            </div>
          </div>
        </div>

        {/* Warning Details */}
        <div className="space-y-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <h4 className="font-medium text-destructive">Abmahnungsgrund</h4>
          
          <div>
            <Label>Kurze Bezeichnung des Verstoßes *</Label>
            <Input 
              value={formData.warningReason} 
              onChange={(e) => setFormData(prev => ({ ...prev, warningReason: e.target.value }))}
              placeholder="z.B. Unentschuldigtes Fehlen, Arbeitsverweigerung, Unpünktlichkeit..."
            />
          </div>

          <div>
            <Label>Detaillierte Beschreibung des Vorfalls *</Label>
            <Textarea 
              value={formData.warningDetails} 
              onChange={(e) => setFormData(prev => ({ ...prev, warningDetails: e.target.value }))}
              placeholder="Beschreiben Sie den Vorfall genau: Datum, Uhrzeit, Ort, was genau vorgefallen ist, welche Zeugen es gibt, etc."
              rows={6}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Hinweis: Je genauer die Beschreibung, desto rechtssicherer ist die Abmahnung.
            </p>
          </div>

          <div>
            <Label>Konsequenzen bei Wiederholung *</Label>
            <Textarea 
              value={formData.warningConsequences} 
              onChange={(e) => setFormData(prev => ({ ...prev, warningConsequences: e.target.value }))}
              rows={3}
            />
          </div>
        </div>

        {/* Signature Section */}
        {showSignature && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h4 className="font-medium">Unterschrift des Arbeitnehmers (Kenntnisnahme)</h4>
            <p className="text-sm text-muted-foreground">
              Die Unterschrift bestätigt nur den Empfang der Abmahnung, nicht deren Anerkennung.
            </p>
            <SignatureCanvas
              onSave={async (signatureDataUrl) => {
                const blob = await fetch(signatureDataUrl).then(r => r.blob());
                const fileName = `warning-signature-${Date.now()}.png`;
                const { data: uploadData, error: uploadError } = await supabase.storage
                  .from("employee-profiles")
                  .upload(`signatures/${fileName}`, blob);

                if (uploadError) throw uploadError;

                const { data: urlData } = supabase.storage
                  .from("employee-profiles")
                  .getPublicUrl(`signatures/${fileName}`);

                await handleSave(urlData.publicUrl);
                setShowSignature(false);
              }}
              title="Unterschrift Mitarbeiter"
              description="Bitte unterschreiben Sie zur Kenntnisnahme der Abmahnung"
              confirmationText="Mit Ihrer Unterschrift bestätigen Sie die Kenntnisnahme."
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={handlePreview} disabled={saving} className="gap-2">
            <Eye className="h-4 w-4" />
            Vorschau
          </Button>
          <Button onClick={() => handleSave()} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Als Entwurf speichern
          </Button>
          <Button variant="outline" onClick={() => setShowSignature(true)} className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            Mit Unterschrift speichern
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <FileDown className="h-4 w-4" />
            PDF exportieren
          </Button>
        </div>
      </CardContent>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfDoc={previewDoc}
        title="Vorschau: Abmahnung"
        fileName={`Abmahnung_${formData.personName.replace(/\s+/g, "_") || "Vorschau"}.pdf`}
      />
    </Card>
  );
};
